/**
 * src/utils/permissions.js
 *
 * Centralized role-check helpers for OnsitePM.
 * All role comparisons in the app should go through these helpers —
 * never compare userProfile.role inline.
 *
 * Roles: admin | pm | assistant | supervisor | coordinator | accountant
 */

/** Returns true if the user has the 'admin' role */
export const isAdmin = (userProfile) =>
  userProfile?.role === 'admin';

/** Returns true if the user has the 'pm' role */
export const isPM = (userProfile) =>
  userProfile?.role === 'pm';

/** Returns true if the user has the 'assistant' role */
export const isAssistant = (userProfile) =>
  userProfile?.role === 'assistant';

/** Returns true if the user has the 'supervisor' role */
export const isSupervisor = (userProfile) =>
  userProfile?.role === 'supervisor';

/** Returns true if the user has the 'coordinator' role */
export const isCoordinator = (userProfile) =>
  userProfile?.role === 'coordinator';

/** Returns true if the user has the 'accountant' role */
export const isAccountant = (userProfile) =>
  userProfile?.role === 'accountant';

/**
 * Returns true if the user can see ALL projects (admin or accountant).
 * Used to gate financial overviews and project-list visibility.
 */
export const canSeeAllProjects = (userProfile) =>
  isAdmin(userProfile) || isAccountant(userProfile);

/**
 * Returns true if the user has cross-project EDIT access.
 * Granted to: admin, accountant, and delegates (can_view_all_projects=true).
 *
 * Use this helper wherever a gate needs "admin OR accountant OR delegate"
 * without requiring project membership. It centralizes the check that is
 * already embedded inside canEditProject and canEditFinancialForProject.
 *
 * @param {object} userProfile - current user's profile
 */
export const canEditAllProjects = (userProfile) => {
  if (!userProfile) return false;
  if (isAdmin(userProfile)) return true;
  if (isAccountant(userProfile)) return true;
  if (userProfile?.can_view_all_projects === true) return true;
  return false;
};

/**
 * Returns true if the user can edit a project's data. * Editable by: admin, the project's PM, the project's assistant, or the project's coordinator.
 *
 * @param {object} project  - project object (camelCase from projectService)
 * @param {object} userProfile - current user's profile
 */
export const canEditProject = (project, userProfile) => {
  if (!project || !userProfile) return false;
  if (isAdmin(userProfile)) return true;
  if (isAccountant(userProfile)) return true;
  if (userProfile?.can_view_all_projects === true) return true;
  // Support both camelCase (from toCamelCase()) and snake_case
  const pmId = project?.pmUserId ?? project?.pm_user_id;
  const assistantId = project?.assistantUserId ?? project?.assistant_user_id;
  const supervisorId = project?.supervisorUserId ?? project?.supervisor_user_id;
  const coordinatorId = project?.coordinatorUserId ?? project?.coordinator_user_id;
  const uid = userProfile?.id;
  return (
    uid === pmId ||
    uid === assistantId ||
    uid === supervisorId ||
    uid === coordinatorId
  );
};
